import { CDS_ROUTE_NAME, DEA_ROUTE_NAME } from '~/features/routeNames';
import { CDSLicense, DEALicense } from '~/types';
import { deleteData, getData, patchData, postData } from '~/utils/service';
import { CdsLicenseFormValues } from '../cds/types';
import { DeaLicenseFormValues } from '../dea/types';
import { cdsApiToForm, cdsApiToForms } from './actions';
import { GetResponse, OnDeleteEvent, OnSubmitCreateEvent, Scope } from './types';

// Server rejects unknown keys on Create/Update; strip server-managed fields first.
const stripServerManagedFields = <T extends Record<string, unknown>>(
  values: T
): Omit<T, 'id' | 'providerId' | 'validFlag' | 'warnings'> => {
  const { id: _id, providerId: _pid, validFlag: _vf, warnings: _w, ...rest } = values;
  return rest;
};

export const CDS_API_CALLS = {
  get: async ({ input }: { input: Scope }) => {
    const cdsTable = input.context.cdsTable;
    const { filter, sort, pagination } = cdsTable;
    const sortOptions = { ...sort, sortBy: sort.active, ...pagination };
    return await getData(CDS_ROUTE_NAME, {
      params: {
        providerId: input.context.providerId,
        active: filter.active,
        inactive: filter.inactive,
      },
      sort: sortOptions,
      transformResponse: cdsApiToForms,
    });
  },
  create: async ({ input }: { input: Scope }) => {
    const { context, event } = input;
    const { values } = event as { values: CdsLicenseFormValues };
    const body = {
      ...stripServerManagedFields(values),
      issuingStateCode: values.issuingStateCode.value,
    } as Omit<CDSLicense, 'id' | 'providerId'>;
    return await postData(CDS_ROUTE_NAME, {
      data: body,
      transformResponse: cdsApiToForm,
      params: { providerId: context.providerId },
    });
  },
  delete: async ({ input }: { input: Scope }) => {
    const { context, event } = input;
    const { payload } = event as OnDeleteEvent;
    return await deleteData(CDS_ROUTE_NAME, {
      pathParams: { id: payload.id.toString() },
      params: { providerId: context.providerId },
    });
  },
  update: async ({ input }: { input: Scope }) => {
    const { context, event } = input;
    const { values } = event as { values: CdsLicenseFormValues };
    const body = {
      ...stripServerManagedFields(values),
      issuingStateCode: values.issuingStateCode.value,
    } as CDSLicense;
    return await patchData(CDS_ROUTE_NAME, {
      pathParams: { id: context.editId.toString() },
      params: { providerId: context.providerId },
      data: body,
      transformResponse: cdsApiToForm,
    });
  },
};

export const DEA_API_CALLS = {
  get: async ({ input }: { input: Scope }) => {
    const deaTable = input.context.deaTable;
    const { filter, sort, pagination } = deaTable;
    const sortOptions = { ...sort, sortBy: sort.active, ...pagination };
    return await getData<GetResponse<DEALicense>>(DEA_ROUTE_NAME, {
      params: {
        providerId: input.context.providerId,
        active: filter.active,
        inactive: filter.inactive,
      },
      sort: sortOptions,
    });
  },
  create: async ({ input }: { input: Scope }) => {
    const { context, event } = input;
    const { values } = event as OnSubmitCreateEvent;
    return await postData(DEA_ROUTE_NAME, {
      data: stripServerManagedFields(values) as Omit<DEALicense, 'id' | 'providerId'>,
      params: { providerId: context.providerId },
    });
  },
  delete: async ({ input }: { input: Scope }) => {
    const { context, event } = input;
    const { payload } = event as OnDeleteEvent;
    return await deleteData(DEA_ROUTE_NAME, {
      pathParams: { id: payload.id.toString() },
      params: { providerId: context.providerId },
    });
  },
  update: async ({ input }: { input: Scope }) => {
    const { context, event } = input;
    const { values } = event as { values: DeaLicenseFormValues };
    return await patchData(DEA_ROUTE_NAME, {
      pathParams: { id: context.editId.toString() },
      params: { providerId: context.providerId },
      data: stripServerManagedFields(values),
    });
  },
};
